import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { SortableContext, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import Add from "@mui/icons-material/Add";
import ChevronRight from "@mui/icons-material/ChevronRight";
import Delete from "@mui/icons-material/Delete";
import DragIndicator from "@mui/icons-material/DragIndicator";
import {
  Box,
  Divider,
  IconButton,
  IconButtonProps,
  ListItemSecondaryAction,
  ListItemText,
  Menu,
  MenuItem,
  MenuItemProps,
  TextField,
} from "@mui/material";
import * as R from "ramda";
import React, { useState } from "react";
import { PROPERTY_COLOR_KEYS, PROPERTY_COLORS } from "../../types/page/propertyColor";

export type SelectOption = { label: string; color?: string };

export const SelectOptionMenu: React.FC<{
  anchorEl: HTMLElement | null;
  options: SelectOption[];
  setOptions: (opts: SelectOption[]) => void;
  open: boolean;
  setOpen: (open: boolean) => void;
  index?: number;
}> = ({ anchorEl, open, setOpen, index, options, setOptions }) => {
  const option = index !== undefined ? options[index] : undefined;

  const setName = (label: string) => {
    if (index === undefined) return;
    const newOption = { ...option, label: option?.label ?? "", color: option?.color };
    setOptions(R.update(index, { ...newOption, label }, options));
  };

  const setColor = (color: string) => {
    if (index === undefined) return;
    const newOption = { ...option, label: option?.label ?? "", color };
    setOptions(R.update(index, newOption, options));
  };

  const onDelete = () => {
    if (index === undefined) return;
    setOptions(R.remove(index, 1, options));
    setOpen(false);
  };

  return (
    <Menu
      onClose={() => setOpen(false)}
      open={open}
      anchorEl={anchorEl}
      anchorOrigin={{ vertical: "top", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
    >
      <MenuItem disabled>
        <ListItemText>Name</ListItemText>
      </MenuItem>
      <Box sx={{ width: "15rem", padding: "1rem" }}>
        <TextField
          onKeyDown={(e) => e.stopPropagation()}
          onChange={(e) => setName(e.target.value)}
          label="name"
          value={option?.label ?? ""}
          inputProps={{ "data-testid": "option-name-field" }}
        />
      </Box>
      <MenuItem disabled>
        <ListItemText>Color</ListItemText>
      </MenuItem>
      <Box sx={{ display: "flex", gap: "0.5rem", padding: "0.5rem 1rem" }}>
        {PROPERTY_COLOR_KEYS.map((key) => (
          <Box
            key={key}
            data-testid={`option-color-${key}`}
            onClick={() => setColor(key)}
            sx={{
              width: "1.25rem",
              height: "1.25rem",
              borderRadius: "50%",
              bgcolor: PROPERTY_COLORS[key].bg,
              cursor: "pointer",
              outline: option?.color === key ? "2px solid #555" : "none",
              outlineOffset: "2px",
            }}
          />
        ))}
      </Box>
      <Divider />
      <MenuItem onClick={() => onDelete()}>
        <Delete color="primary" />
        <ListItemText sx={{ ml: 1 }}>Delete</ListItemText>
      </MenuItem>
    </Menu>
  );
};

export const PropertyViewTypeOptionsSelectOption: React.FC<
  {
    index: number;
    option: SelectOption;
    openIconProps?: IconButtonProps;
  } & MenuItemProps
> = ({ index: i, option, openIconProps, ...menuItemProps }) => {
  const { attributes, listeners, setNodeRef, transform } = useSortable({ id: i });
  const style = { transform: CSS.Translate.toString(transform), cursor: "grab" };

  return (
    <MenuItem {...menuItemProps} ref={setNodeRef} {...attributes} style={style}>
      <DragIndicator color="primary" {...listeners} />
      <ListItemText>{option.label}</ListItemText>
      <ListItemSecondaryAction>
        <IconButton {...openIconProps} data-testid={`option-chevron-${i}`}>
          <ChevronRight color="primary" />
        </IconButton>
      </ListItemSecondaryAction>
    </MenuItem>
  );
};

export const PropertyViewTypeOptionsSelect: React.FC<{
  options: SelectOption[];
  setOptions: (opts: SelectOption[]) => void;
}> = ({ options, setOptions }) => {
  const [optionMenuOpen, setOptionMenuOpen] = useState(false);
  const [optionMenuAnchor, setOptionMenuAnchor] = useState<HTMLElement | null>(null);
  const [selectedOptionIndex, setSelectedOptionIndex] = useState<number | undefined>(0);
  const sensors = useSensors(useSensor(PointerSensor));

  const onAdd = () => {
    setOptions([...options, { label: `Option ${options.length + 1}` }]);
  };

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over) return;
    const ai = Number(active.id);
    const oi = Number(over.id);
    if (ai === oi) return;
    setOptions(R.pipe(
      R.update(ai, options[oi]),
      R.update(oi, options[ai]),
    )(options) as SelectOption[]);
  };

  return (
    <>
      <MenuItem disabled>
        <ListItemText>Options</ListItemText>
      </MenuItem>
      <SelectOptionMenu
        options={options}
        setOptions={setOptions}
        open={optionMenuOpen}
        setOpen={setOptionMenuOpen}
        anchorEl={optionMenuAnchor}
        index={selectedOptionIndex}
      />
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={options.map((_, i) => i)}>
          {options.map((option, i) => (
            <PropertyViewTypeOptionsSelectOption
              option={option}
              index={i}
              key={i}
              onClick={(e) => {
                setOptionMenuAnchor(e.currentTarget);
                setOptionMenuOpen(true);
                setSelectedOptionIndex(i);
              }}
            />
          ))}
        </SortableContext>
      </DndContext>
      <Divider />
      <MenuItem onClick={onAdd} data-testid="options-add-btn">
        <ListItemText>Add</ListItemText>
        <ListItemSecondaryAction>
          <IconButton><Add color="primary" /></IconButton>
        </ListItemSecondaryAction>
      </MenuItem>
    </>
  );
};
